<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Migração Mercado Pago</v-toolbar-title>
                    <v-spacer/>
                    <v-btn icon :loading="carregando" @click="recarregar" aria-label="Atualizar">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-alert v-if="!podeConsultar" type="info" text class="ma-2">
                    Você não tem permissão para ver a migração do Mercado Pago.
                </v-alert>

                <v-card v-else elevation="2" class="ma-1">
                    <v-card-text v-if="prazo" class="pb-0">
                        <v-alert
                            v-if="!prazo.em"
                            type="warning"
                            text
                            dense
                            class="mb-0"
                        >
                            Prazo final da migração não configurado no servidor (MERCADO_PAGO_MIGRACAO_PRAZO).
                        </v-alert>

                        <v-alert
                            v-else-if="prazo.vencido"
                            type="error"
                            text
                            class="mb-0"
                        >
                            <div class="d-flex flex-wrap align-center" style="gap: 8px;">
                                <div class="flex-grow-1">
                                    O token colado não é usado desde {{ formatDate(prazo.em, 'DD/MM/YYYY [às] HH:mm') }}.
                                    {{ prazo.legado_pendentes }} empresa(s) da chave de acesso estão sem cobrança PIX automática.
                                </div>
                                <v-btn small outlined color="error" @click="verPendentesLegado">Ver empresas</v-btn>
                            </div>
                        </v-alert>

                        <v-alert v-else type="info" text class="mb-0" icon="mdi-clock-outline">
                            <div class="font-weight-medium">
                                Prazo final da migração: {{ formatDate(prazo.em, 'DD/MM/YYYY [às] HH:mm') }}
                                · {{ textoDiasRestantes }}
                            </div>
                            <div class="body-2">
                                A partir daí, a tela fica obrigatória para todas as empresas da campanha e o token colado
                                deixa de ser usado: quem ainda usa chave de acesso fica sem cobrança PIX automática.
                            </div>
                            <div class="caption">Definido no servidor em MERCADO_PAGO_MIGRACAO_PRAZO.</div>
                        </v-alert>
                    </v-card-text>

                    <v-tabs v-model="aba" class="px-2">
                        <v-tab>Progresso</v-tab>
                        <v-tab>Empresas</v-tab>
                    </v-tabs>
                    <v-divider/>

                    <v-tabs-items v-model="aba" touchless>
                        <v-tab-item>
                            <TabProgresso
                                :resumo="resumo"
                                :carregando="carregando"
                                @ver-empresa="verEmpresa"
                            />
                        </v-tab-item>
                        <!-- eager: o "Ver empresa" da aba Progresso filtra esta aba antes de ela ser aberta. -->
                        <v-tab-item eager>
                            <TabEmpresas
                                ref="empresas"
                                :ondas="ondasNomes"
                                @mudou="carregarResumo"
                            />
                        </v-tab-item>
                    </v-tabs-items>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import TabProgresso from './TabProgresso';
import TabEmpresas from './TabEmpresas';

/**
 * Campanha de migração do Mercado Pago (zaper-api/MERCADO_PAGO_MIGRACAO_PLAN.md, Fase 4):
 * aba Progresso (resumo) e aba Empresas (lista e liberação por onda).
 */
export default {
    name: 'MpMigracao',

    components: { BaseContainer, TabProgresso, TabEmpresas },

    data: () => ({
        aba: 0,
        carregando: false,
        resumo: {},
    }),

    computed: {
        podeConsultar() {
            return this.checkPermission('zapercontrol_menu_mp_migracao') || this.isDiretor();
        },

        prazo() {
            return this.resumo.prazo || null;
        },

        ondasNomes() {
            return (this.resumo.ondas || []).map(o => o.onda);
        },

        textoDiasRestantes() {
            const dias = this.prazo ? this.prazo.dias_restantes : 0;

            if (!dias) {
                return 'último dia';
            }

            return dias === 1 ? 'falta 1 dia' : `faltam ${dias} dias`;
        },
    },

    mounted() {
        this.podeConsultar && this.carregarResumo();
    },

    methods: {
        async carregarResumo() {
            this.carregando = true;

            try {
                const { data } = await this.$http.get('mercadopago-migracao/resumo');

                if (data.type !== 'success') {
                    this.notify(data.msg || 'Não foi possível carregar o progresso', 'warning');
                    return;
                }

                this.resumo = data.data || {};
            } catch (e) {
                this.notify('Não foi possível carregar o progresso', 'warning');
            } finally {
                this.carregando = false;
            }
        },

        recarregar() {
            this.carregarResumo();
            this.$refs.empresas && this.$refs.empresas.carregar();
        },

        verEmpresa(empresaId) {
            this.aba = 1;
            this.$nextTick(() => this.$refs.empresas.filtrar({ busca: String(empresaId), situacao: 'todas', cenario: null, onda: null }));
        },

        verPendentesLegado() {
            this.aba = 1;
            this.$nextTick(() => this.$refs.empresas.filtrar({ busca: '', situacao: 'pendentes', cenario: 'legado_oauth', onda: null }));
        },
    },
};
</script>
